export const DEFAULT_RANGE = { min: 100, max: 500, step: 50 };
export const MAX_BUCKETS = 60;

export function validateRange({ min, max, step }) {
  if (![min, max, step].every(Number.isFinite))
    return "请填写完整、有效的价格数字。";
  if (min < 0 || max < 0) return "价格不能小于 0。";
  if (max <= min) return "最高价格必须大于最低价格。";
  if (step <= 0) return "分段间隔必须大于 0。";
  if (Math.ceil((max - min) / step) > MAX_BUCKETS)
    return `最多支持 ${MAX_BUCKETS} 个价格段，请增加间隔或缩小范围。`;
  return "";
}

// 使用整数“分”运算，避免 0.1 + 0.2 之类的浮点边界误差。
export function createBuckets(range) {
  const error = validateRange(range);
  if (error) throw new Error(error);
  const min = Math.round(range.min * 100),
    max = Math.round(range.max * 100),
    step = Math.round(range.step * 100);
  if (step < 1 || max <= min) throw new Error("价格和间隔最多支持两位小数。");
  const count = Math.ceil((max - min) / step);
  if (count > MAX_BUCKETS)
    throw new Error(`最多支持 ${MAX_BUCKETS} 个价格段。`);
  return Array.from({ length: count }, (_, i) => ({
    id: `${min + i * step}-${Math.min(min + (i + 1) * step, max)}`,
    lower: (min + i * step) / 100,
    upper: Math.min(min + (i + 1) * step, max) / 100,
    includeUpper: i === count - 1,
  })).reverse();
}

export function isInBucket(price, bucket) {
  const cents = Math.round(price * 100);
  return (
    cents >= Math.round(bucket.lower * 100) &&
    (bucket.includeUpper
      ? cents <= Math.round(bucket.upper * 100)
      : cents < Math.round(bucket.upper * 100))
  );
}

export function isValidDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
}

export function validateLaunchDates(start, end) {
  if ((start && !isValidDate(start)) || (end && !isValidDate(end)))
    return "请填写有效的上市日期。";
  if (start && end && start > end) return "上市开始日期不能晚于结束日期。";
  return "";
}

// 每个二元组为 [RAM(GB), ROM/存储(GB)]；兼容旧的单组字段。
export function getProductConfigurations(product) {
  const source = Array.isArray(product.configurations)
    ? product.configurations
    : [[product.ram, product.storage]];
  const seen = new Set();
  return source.filter((pair) => {
    if (
      !Array.isArray(pair) ||
      pair.length !== 2 ||
      !pair.every((n) => Number.isFinite(n) && n > 0)
    )
      return false;
    const key = pair.join(":");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function formatConfigurations(
  product,
  fields = { ram: true, storage: true },
) {
  if (!fields.ram && !fields.storage) return "";
  return getProductConfigurations(product)
    .map(([ram, rom]) =>
      [fields.ram && `${ram}GB`, fields.storage && `${rom}GB`]
        .filter(Boolean)
        .join(" + "),
    )
    .join(" | ");
}

export function filterProducts(products, filters, range) {
  const query = filters.query.trim().toLocaleLowerCase();
  const { launchStart = "", launchEnd = "" } = filters;
  if (validateLaunchDates(launchStart, launchEnd)) return [];
  return products
    .filter(
      (p) =>
        filters.brandIds.includes(p.brandId) &&
        p.price >= range.min &&
        p.price <= range.max &&
        (!query ||
          `${p.name} ${p.brandId} ${getProductConfigurations(p)
            .map(([ram, rom]) => `${ram}GB+${rom}GB`)
            .join(" ")}`
            .toLocaleLowerCase()
            .includes(query)) &&
        (!(filters.ram || filters.storage) ||
          getProductConfigurations(p).some(
            ([ram, rom]) =>
              (!filters.ram || ram === Number(filters.ram)) &&
              (!filters.storage || rom === Number(filters.storage)),
          )) &&
        (!filters.network || p.network === filters.network) &&
        (!filters.country || p.country === filters.country) &&
        (!(launchStart || launchEnd) ||
          (isValidDate(p.launchDate) &&
            (!launchStart || p.launchDate >= launchStart) &&
            (!launchEnd || p.launchDate <= launchEnd))),
    )
    .sort((a, b) => b.price - a.price || a.id.localeCompare(b.id));
}

export function moveBrand(order, source, target) {
  const from = order.indexOf(source),
    to = order.indexOf(target);
  if (from < 0 || to < 0 || from === to) return order;
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, source);
  return next;
}

export const formatPrice = (n) =>
  Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
